/**
 * API client for the auth endpoints.
 *
 * All session management is cookie-based (HTTP-only, SameSite=Strict).
 * The client never stores tokens in localStorage or sessionStorage.
 *
 * Endpoints:
 *   POST /api/v1/auth/signup
 *   POST /api/v1/auth/login
 *   POST /api/v1/auth/logout
 *   GET  /api/v1/auth/me
 *   POST /api/v1/auth/forgot-password
 *   POST /api/v1/auth/reset-password
 *   POST /api/v1/auth/google/link-confirm   (WI-39b)
 */

export interface AuthUser {
  user_id: string;
  email: string;
}

export interface ApiError {
  error: string;
  code: string;
}

/** Thrown when the server returns a non-2xx response. */
export class AuthApiError extends Error {
  public readonly code: string;
  public readonly status: number;

  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = 'AuthApiError';
    this.code = code;
    this.status = status;
  }
}

async function parseError(response: Response): Promise<AuthApiError> {
  try {
    const body: ApiError = await response.json();
    return new AuthApiError(body.error, body.code, response.status);
  } catch {
    return new AuthApiError(response.statusText, 'unknown', response.status);
  }
}

/**
 * Create a new user account. Returns the created user on success.
 * Throws AuthApiError on 409 (duplicate email) or 422 (validation).
 */
export async function signup(email: string, password: string): Promise<AuthUser> {
  const response = await fetch('/api/v1/auth/signup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  });
  if (!response.ok) throw await parseError(response);
  return response.json();
}

/**
 * Log in with email and password. Returns the authenticated user on success.
 * Throws AuthApiError on 401 (bad credentials) or 429 (rate limited).
 */
export async function login(email: string, password: string): Promise<AuthUser> {
  const response = await fetch('/api/v1/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  });
  if (!response.ok) throw await parseError(response);
  return response.json();
}

/**
 * Log out the current user. Always resolves (even if no session exists).
 */
export async function logout(): Promise<void> {
  await fetch('/api/v1/auth/logout', {
    method: 'POST',
    credentials: 'include',
  });
}

/**
 * Returns the currently authenticated user, or null if not logged in.
 * Does NOT throw on 401 — returns null instead.
 */
export async function getMe(): Promise<AuthUser | null> {
  const response = await fetch('/api/v1/auth/me', {
    credentials: 'include',
  });
  if (response.status === 401) return null;
  if (!response.ok) throw await parseError(response);
  return response.json();
}

/**
 * Request a password reset email. Always resolves with a success message
 * regardless of whether the email is registered (TC-008: no user enumeration).
 */
export async function forgotPassword(email: string): Promise<void> {
  const response = await fetch('/api/v1/auth/forgot-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email }),
  });
  if (!response.ok) throw await parseError(response);
}

/**
 * Reset a password using a token from the email link.
 * Throws AuthApiError on 400 (invalid/used token) or 410 (expired token).
 */
export async function resetPassword(token: string, newPassword: string): Promise<void> {
  const response = await fetch('/api/v1/auth/reset-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ token, new_password: newPassword }),
  });
  if (!response.ok) throw await parseError(response);
}

/**
 * Confirm Google account linking.
 *
 * Called from LinkAccountPage when the user clicks "Link accounts".
 * The backend reads `pending_oauth_link` from the session (set during the
 * Google OAuth callback), creates the user_identities row, and issues a session.
 * Returns `{ return_to }` — the return URL to navigate to after linking.
 *
 * Throws AuthApiError on:
 *   - 400: no pending_oauth_link in session (stale page load / session expired)
 *   - 401: session not found
 *
 * AC-5 (WI-39b): called from LinkAccountPage with credentials: 'include'.
 */
export interface LinkConfirmResult {
  return_to: string;
}

export async function linkGoogleAccount(): Promise<LinkConfirmResult> {
  const response = await fetch('/api/v1/auth/google/link-confirm', {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) throw await parseError(response);
  return response.json();
}
